import { Card, Container, Flex, Text } from "@radix-ui/themes";
import { useEffect, useRef, useState } from "react";
import logo from "../assets/logo.png";
import { usePostHog } from "../hooks/usePostHog";
import { useUserDevices } from "../hooks/useUserDevices";
import type { ClientSession } from "../types/auth"; // Ensure Device is imported from useUserDevices or centralized types
import RemoteSetupFlow from "./onboarding/RemoteSetupFlow";
import Footer from "./shared/Footer";

interface AuthCompleteViewProps {
    session: ClientSession;
    /** Completes the OAuth session. `redirect: false` completes without navigating to the client. */
    /** Assistant that started the OAuth run ("ChatGPT" | "Claude"), or null when unrecognised. */
    client?: string | null;
    redirectWithToken: (redirect?: boolean) => Promise<void>;
    handleSignOut: () => void;
}

const AuthCompleteView = ({ session, client, redirectWithToken, handleSignOut }: AuthCompleteViewProps) => {
    const { track, trackOnce } = usePostHog();
    const hasTrackedPageView = useRef(false);

    // Fetch devices and loading state only for tracking
    const { devices, loading: devicesLoading } = useUserDevices();

    const devicesLoaded = !devicesLoading;
    const hasOnlineDevices = devices && devices.length > 0 && devices.some((device) => device.status === "online");

    const [isRedirecting, setIsRedirecting] = useState(false);
    const hasAutoCompleted = useRef(false);

    // Track page view with critical business context (only once, after devices load)
    useEffect(() => {
        if (devicesLoaded && session?.user && !hasTrackedPageView.current) {
            hasTrackedPageView.current = true;
            trackOnce("page_auth_complete_viewed", session.user.id, {
                user_id: session.user.id,
                email: session.user.email,
                has_online_devices: hasOnlineDevices,
                device_count: devices?.length || 0,
            });
        }
    }, [
        devicesLoaded,
        session?.user,
        trackOnce,
        hasOnlineDevices,
        devices?.length,
    ]);
    const handleContinue = async () => {
        setIsRedirecting(true);
        try {
            // oauth_redirect_completed is emitted once in redirectWithToken (useOAuthCompletion),
            // where it carries redirect_uri for client attribution — don't double-fire it here.
            //
            // Preserve the standard OAuth handoff: complete the session and let this setup tab
            // follow the connector's redirect_uri back to ChatGPT, Claude, or another client.
            // The user-facing finish line remains the separate verification tab they are already
            // looking at; this background tab is only responsible for completing the OAuth return.
            await redirectWithToken(true);
        } catch (error) {
            console.error("Redirect failed:", error);

            // Track OAuth redirect failure
            track("oauth_redirect_failed", {
                user_id: session.user.id,
                email: session.user.email,
                error_message: error instanceof Error ? error.message : "Unknown error",
            });

            setIsRedirecting(false);
        }
    };

    // The device coming online is the only real signal this page gets, and it means the user has
    // already verified in the other tab. Nothing is left for them to click here, so finish by
    // ourselves instead of parking on a "Finish setup" button in a tab they have no reason to revisit.
    useEffect(() => {
        if (!hasOnlineDevices || hasAutoCompleted.current) {
            return;
        }
        hasAutoCompleted.current = true;
        setIsRedirecting(true);
        void handleContinue();
        // handleContinue is stable enough here: it only closes over session and redirectWithToken.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        hasOnlineDevices,
    ]);

    return (
        <Container
            size="2"
            p="4"
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                // stretch, not center: centering here sizes Radix's inner Container wrapper to its
                // content, so the card would change width whenever the help list opened.
                alignItems: "stretch",
                // Optical centering — consistent with the other auth-flow screens.
                paddingBottom: "12vh",
            }}
        >
            {/* Brand sits above the card, as in the prototype — the card holds the flow and nothing else.
                Centred by its own margin: Radix Container renders an inner wrapper, so the flex
                centering on the Container itself does not reach these children. */}
            <img
                src={logo}
                alt="Desktop Commander Remote"
                style={{
                    display: "block",
                    width: 52,
                    height: 52,
                    objectFit: "contain",
                    margin: "0 auto 18px",
                }}
            />
            {/* 560px matches the prototype's shell, so line breaks land where they did in the sketch. */}
            <Card
                size="4"
                style={{
                    width: "100%",
                    maxWidth: 560,
                    margin: "0 auto",
                }}
            >
                <Flex direction="column" align="stretch">
                    {isRedirecting ? (
                        <Text size="2" color="green" weight="medium" align="center">
                            Device connected. Finishing setup…
                        </Text>
                    ) : (
                        devicesLoaded && (
                            <RemoteSetupFlow
                                client={client}
                                source="oauth_setup"
                                userEmail={session.user.email ?? ""}
                                userId={session.user.id}
                                onSignOut={handleSignOut}
                                deviceCount={devices?.length ?? 0}
                            />
                        )
                    )}
                </Flex>
            </Card>
            <Footer />
        </Container>
    );
};

export default AuthCompleteView;
